<template>
  <div>
    <div class="zoom-actions">
      <v-btn @click="zoomIn">+</v-btn>
      <v-btn class="ml-2" @click="zoomOut">-</v-btn>
      <v-btn class="ml-2" @click="reset">
        <v-icon class="mr-1" color="icon" small>mdi-restart</v-icon>
      </v-btn>
      <v-btn v-if="canTransformToV2" class="ml-2" @click="confirmTransformToV2">
        {{ $t("shortcodes.live_chat.transform_to_v2") }}
      </v-btn>
    </div>

    <div class="json-action">
      <v-btn @click="collapseJsonPreview">
        <v-icon> mdi-code-json</v-icon>
      </v-btn>
    </div>

    <div v-if="displayJsonPreview" class="json-preview">
      <div class="json-preview__header">
        <div v-if="jsonFormatError" class="d-flex align-center mb-3">
          <span class="json-preview__format-error">{{ $t("shortcodes.live_chat_auto.invalid_json_format") }}</span>
          <v-tooltip top>
            <template #activator="{ on }">
              <v-btn v-on="on" @click="undoJson" class="ml-3">
                <v-icon>mdi-undo</v-icon>
              </v-btn>
            </template>
            <p>{{ $t("shortcodes.live_chat_auto.undo_to_previous") }}</p>
          </v-tooltip>
        </div>
      </div>
      <code-input
        :class="{ 'code-error': jsonFormatError }"
        :key="jsonInputKey"
        v-model="liveChatDataJsonFormat"
        :title="''"></code-input>
    </div>

    <div class="panzoom-parent">
      <div ref="panzoom">
        <div class="d-flex justify-center">
          <div class="chatbot-data">
            <div class="title mb-2">{{ $t("shortcodes.live_chat_mp4.agent_media") }}</div>
            <VaVideoFileInput
              v-model="liveChatMp4Data.waitingLoopMp4Url"
              :file-id.sync="liveChatMp4Data.waitingLoopMp4FileId"
              class="mb-3 panzoom-ignore"
              :label="$t('shortcodes.live_chat_mp4.waiting_loop_mp4_url')" />
            <div v-if="liveChatMp4Data.waitingLoopMp4Url" class="d-flex align-center mb-3">
              <v-text-field
                class="panzoom-ignore mr-3"
                v-model.number="liveChatMp4Data.waitingLoopVideoOffsetX"
                type="number"
                :label="$t('shortcodes.live_chat_mp4.video_offset_x')"
                persistent-placeholder
                placeholder="0"></v-text-field>
              <v-text-field
                class="panzoom-ignore"
                v-model.number="liveChatMp4Data.waitingLoopVideoOffsetY"
                type="number"
                :label="$t('shortcodes.live_chat_mp4.video_offset_y')"
                persistent-placeholder
                placeholder="0"></v-text-field>
            </div>
            <VaMediaObjectFileInput
              :label="$t('shortcodes.live_chat_mp4.avatar')"
              class="mb-3 panzoom-ignore"
              :item-id="liveChatMp4Data.botAvatar.imageID"
              @update:photo="updateAvatar($event)" />
            <v-text-field
              v-model.number="liveChatMp4Data.maxAvatarSize"
              class="mb-4 panzoom-ignore"
              type="number"
              :label="$t('shortcodes.live_chat_mp4.max_avatar_size')"
              persistent-placeholder
              placeholder="240"></v-text-field>

            <v-select
              v-model="liveChatMp4Data.videoPreloadMode"
              class="mb-4 panzoom-ignore"
              :items="videoPreloadModeItems"
              :label="$t('shortcodes.live_chat_mp4.video_preload_mode')"
              persistent-placeholder></v-select>

            <VaColorPicker
              class="mb-4 panzoom-ignore"
              :title="'Action items background color'"
              v-model="liveChatMp4Data.actionItemBackgroundColor"></VaColorPicker>
            <VaColorPicker
              class="mb-4 panzoom-ignore"
              :title="$t('shortcodes.live_chat_mp4.message_background')"
              v-model="liveChatMp4Data.messageBackgroundColor"></VaColorPicker>

            <div class="title mb-2 mt-4">{{ $t("shortcodes.live_chat_mp4.top_pills_and_typing") }}</div>
            <v-text-field
              v-model="liveChatMp4Data.waitingPillLabel"
              class="mb-3 panzoom-ignore"
              :label="$t('shortcodes.live_chat_mp4.waiting_pill_label')"
              persistent-placeholder
              placeholder="waiting after you"></v-text-field>
            <v-text-field
              v-model.number="liveChatMp4Data.typingSpeed"
              class="mb-3 panzoom-ignore"
              type="number"
              :label="$t('shortcodes.live_chat_mp4.typing_speed')"
              persistent-placeholder
              placeholder="50"></v-text-field>

            <v-text-field
              v-model="liveChatMp4Data.loaderMessage"
              class="mb-4 panzoom-ignore"
              persistent-placeholder
              :label="$t('shortcodes.live_chat_mp4.loader_message')"
              placeholder="Mary"></v-text-field>

            <div class="d-flex justify-center">
              <v-btn :color="'primary'" class="mr-2" v-if="displayAddMessageBtn" @click="addMessage">
                {{ $t("shortcodes.live_chat_auto.add_message") }}
              </v-btn>
            </div>
          </div>
        </div>
        <div class="message-tree">
          <LiveChatMp4Tree :key="liveChatTreeKey" :message-tree="liveChatMp4Data.messages"></LiveChatMp4Tree>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import LiveChatMp4Tree from "@/components/shortcodes/livechatMp4/LiveChatMp4Tree.vue";
  import Panzoom from "@panzoom/panzoom";
  import CodeInput from "@/components/custom/CodeInput";
  import VaColorPicker from "@/components/custom/VaColorPicker";
  import VaVideoFileInput from "@/components/custom/VaVideoFileInput";
  import debounce from "lodash.debounce";
  import cloneDeep from "lodash/cloneDeep";
  import {
    MESSAGE_MP4,
    LIVE_CHAT_MP4_ROOT_DEFAULTS,
    MESSAGE_MP4_DEFAULTS,
    LIVE_CHAT_MP4_ACTION_DEFAULTS,
    LIVE_CHAT_MP4_ACTION_ITEM_DEFAULTS,
    VIDEO_PRELOAD_MODE_OPTIONS,
  } from "@/components/shortcodes/livechatMp4/constant";
  import definedAbility from "@/ability";
  import { convertAssetsPathToAbsoluteLinks, toAbsoluteAssetUrl } from "@/utility/convertAssetsPaths";
  import { traverseTree } from "@/utility/traverseTree";
  import { syncTelHrefErrors } from "@/utility/validateLiveChatTelHrefs";
  import { showConfirmModal } from "@/assets/utilities/tinymce/helpers/telConfirmModal";

  const origin = window.location.origin;

  export default {
    name: "LiveChatMp4Dialog",
    components: { LiveChatMp4Tree, CodeInput, VaColorPicker, VaVideoFileInput },
    data() {
      return {
        ability: definedAbility(),
        panzoomInstance: null,
        liveChatMp4Data: cloneDeep(LIVE_CHAT_MP4_ROOT_DEFAULTS),
        displayJsonPreview: false,
        jsonFormatError: false,
        jsonInputKey: 0,
        liveChatTreeKey: 0,
      };
    },
    computed: {
      canTransformToV2() {
        return this.$route.query.data !== "null" && this.ability.can("view", "all shortcodes");
      },
      displayAddMessageBtn() {
        return this.liveChatMp4Data.messages && this.liveChatMp4Data.messages.length === 0;
      },
      videoPreloadModeItems() {
        return VIDEO_PRELOAD_MODE_OPTIONS.map(opt => ({ value: opt.value, text: this.$t(opt.textKey) }));
      },
      liveChatDataJsonFormat: {
        get() {
          return JSON.stringify(this.liveChatMp4Data, null, " ");
        },
        set(value) {
          let parsedJson;
          try {
            this.jsonFormatError = false;
            parsedJson = JSON.parse(value);
          } catch (e) {
            this.jsonFormatError = true;
          }

          if (!this.jsonFormatError) {
            this.rerenderMessageTree();
            Object.keys(this.liveChatMp4Data).forEach(key => {
              if (parsedJson[key] !== undefined) {
                this.liveChatMp4Data[key] = parsedJson[key];
              }
            });
          }
        },
      },
    },
    watch: {
      liveChatMp4Data: {
        handler(newValue) {
          this.updateValue(newValue);
        },
        deep: true,
      },
    },
    beforeMount() {
      this.setupReceiveDataListener();
      this.requestData();
    },
    mounted() {
      this.panzoomInstance = Panzoom(this.$refs.panzoom, {
        canvas: true,
        excludeClass: "panzoom-ignore",
        handleStartEvent: () => {},
      });

      this.$refs.panzoom && (this.$refs.panzoom.style.height = `${window.innerHeight - 24}px`);

      const parent = this.$refs.panzoom.parentElement;
      parent.addEventListener("wheel", event => {
        if (!event.shiftKey) {
          this.panzoomInstance.pan(-event.deltaX, -event.deltaY, { relative: true });
          return;
        }
        this.panzoomInstance.zoomWithWheel(event);
      });
    },
    methods: {
      addMessage() {
        const message = cloneDeep(MESSAGE_MP4);
        this.liveChatMp4Data.messages.push(message);
      },
      rerenderMessageTree: debounce(function () {
        this.liveChatTreeKey++;
      }, 1000),
      reset() {
        this.panzoomInstance.reset();
      },
      zoomIn() {
        this.panzoomInstance.zoomIn();
      },
      zoomOut() {
        this.panzoomInstance.zoomOut();
      },
      confirmTransformToV2() {
        showConfirmModal({
          title: this.$t("shortcodes.live_chat.transform_to_v2"),
          message: this.$t("shortcodes.live_chat_mp4.transform_to_v2_confirm"),
          confirmLabel: this.$t("shortcodes.live_chat.transform_to_v2_confirm_button"),
        }).then(confirmed => {
          if (confirmed) {
            window.parent.postMessage({ mceAction: "transformToV2", data: this.liveChatMp4Data }, origin);
          }
        });
      },
      updateAvatar(value) {
        if (value) {
          this.liveChatMp4Data.botAvatar.imageID = value["@id"];
          this.liveChatMp4Data.botAvatar.imageURL = value.contentUrl || "";
        } else {
          this.liveChatMp4Data.botAvatar.imageID = "";
          this.liveChatMp4Data.botAvatar.imageURL = "";
        }
      },
      collapseJsonPreview() {
        this.displayJsonPreview = !this.displayJsonPreview;
      },
      undoJson() {
        this.liveChatDataJsonFormat = JSON.stringify(this.liveChatMp4Data, null, " ");
        this.jsonInputKey++;
      },
      updateValue(val) {
        const data = { ...val };
        window.parent.postMessage(
          {
            mceAction: "updateLiveChatData",
            data,
          },
          origin,
        );
      },
      syncTelHrefErrors() {
        syncTelHrefErrors(this.liveChatMp4Data.messages, this);
      },
      setupReceiveDataListener() {
        window.onmessage = message => {
          if (!message.data || typeof message.data !== "object") return;

          if (message.data.type === "telHrefValidationFailed") {
            this.syncTelHrefErrors();
            return;
          }

          if (message.data.type === "sendData") {
            const { data } = message.data;

            const normalized = { ...cloneDeep(LIVE_CHAT_MP4_ROOT_DEFAULTS), ...data };
            if (!normalized.messages) normalized.messages = [];
            if (!normalized.botAvatar) normalized.botAvatar = { imageID: "", imageURL: "" };

            // Convert relative asset paths (stored in shortcode) to absolute URLs for editing UI inputs.
            normalized.waitingLoopMp4Url = toAbsoluteAssetUrl(normalized.waitingLoopMp4Url);
            if (normalized.botAvatar?.imageURL) {
              normalized.botAvatar.imageURL = toAbsoluteAssetUrl(normalized.botAvatar.imageURL);
            }

            traverseTree(normalized.messages, node => {
              if (node.content) node.content = convertAssetsPathToAbsoluteLinks(node.content);
              if (node.text) node.text = convertAssetsPathToAbsoluteLinks(node.text);
              if (node.value) node.value = convertAssetsPathToAbsoluteLinks(node.value);
              if (node.mp4Url) node.mp4Url = toAbsoluteAssetUrl(node.mp4Url);
            });

            if (normalized.loaderMessage === undefined && normalized.botName !== undefined) {
              normalized.loaderMessage = normalized.botName;
            }
            if (normalized.messageBackgroundColor === undefined && normalized.userMessageBackgroundColor !== undefined) {
              normalized.messageBackgroundColor = normalized.userMessageBackgroundColor;
            }

            this.liveChatMp4Data = normalized;

            // Backward compat: ensure nodes have MP4 fields
            traverseTree(this.liveChatMp4Data.messages, node => {
              if (node.messageType === "message") {
                Object.keys(MESSAGE_MP4_DEFAULTS).forEach(key => {
                  if (node[key] === undefined) this.$set(node, key, MESSAGE_MP4_DEFAULTS[key]);
                });
              } else if (node.messageType === "action") {
                Object.keys(LIVE_CHAT_MP4_ACTION_DEFAULTS).forEach(key => {
                  if (node[key] === undefined) this.$set(node, key, LIVE_CHAT_MP4_ACTION_DEFAULTS[key]);
                });
              } else if (node.messageType === "actionItem") {
                Object.keys(LIVE_CHAT_MP4_ACTION_ITEM_DEFAULTS).forEach(key => {
                  if (node[key] === undefined) this.$set(node, key, LIVE_CHAT_MP4_ACTION_ITEM_DEFAULTS[key]);
                });
              }
            });
          }
        };
      },
      requestData() {
        window.parent.postMessage(
          {
            mceAction: "requestData",
          },
          origin,
        );
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "@core/preset/preset/typography/index.scss";
  @import "@core/preset/preset/functions.scss";

  .zoom-actions {
    position: absolute;
    top: 20px;
    left: 20px;
    z-index: 100;
  }

  .json-action {
    position: fixed;
    top: 20px;
    right: 20px;
    z-index: 102;
  }

  .title {
    color: get-color("subtitle");
    @include heading-4();
  }

  .chatbot-data {
    border: 1px dashed get-color("primary");
    border-radius: 5px;
    background: get-color("element-bg-100");
    padding: 5px 10px;
    max-width: 600px;
    min-width: 600px;
  }

  .message-tree {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);

    .message-item {
      border: 1px dashed get-color("primary");
      border-radius: 5px;
      background: get-color("element-bg-100");
      padding: 5px 10px;

      &__settings-wrapper {
        display: flex;
        align-items: center;
      }

      .message-item__settings {
        &--delay {
          max-width: 100px;
        }

        &--human {
          margin-left: 10px;
        }

        &--no-icon {
          margin-left: 10px;
        }
      }
    }
  }

  .json-preview {
    position: absolute;
    top: 0;
    left: 5px;
    right: 5px;
    bottom: 5px;
    z-index: 101;
    background-color: get-color("element-bg-200");

    &__header {
      display: flex;
      background: get-color("element-bg-200");
      padding: 70px 20px 0;

      &-title {
        color: get-color("subtitle");
        @include text-body-1-bold();
      }
    }

    &__format-error {
      color: get-color("error");
      @include text-body-1-bold();
    }
  }

  .code-error {
    border: 3px dashed get-color("error") !important;
  }
</style>
